import { useState, useEffect } from "react";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport
} from "@mui/x-data-grid";
import { Box, Chip } from "@mui/material";
import { useTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";

// Components
import { useNotification } from "../utils/Notification";
import Message from "../modals/Message";
import {
  getLocalTimestamp,
  generateColorForChip,
  formatDate,
  trackEvent,
} from "../utils/Utils";

// Theme
import { tokens } from "../../theme";

// Supabase
import supabase from "../../supabaseClient";

export default function History() {
  const theme = useTheme();
  const { t } = useTranslation();
  const colors = tokens(theme.palette.mode);
  const [messages, setMessages] = useState([]);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);

  // Context
  const { showNotification } = useNotification();

  const typeMapping = {
    me: t("upcoming.me"),
    group: t("upcoming.group"),
    bulk: t("upcoming.bulk"),
    contact: t("upcoming.contact"),
    manual: t("upcoming.manual"),
  };

  const messageStatusMapping = {
    sent: t("upcoming.sent"),
    partial_sent: t("upcoming.partial_sent"),
  };

  const columns = [
    {
      field: "type",
      headerName: t("history.type"),
      width: 110,
      valueGetter: (params) => typeMapping[params.row.type] || params.row.type,
    },
    { field: "to", headerName: t("history.to"), width: 200 },
    { field: "message", headerName: t("history.message"), width: 300 },
    { field: "file", headerName: t("history.file"), width: 200 },
    {
      field: "label",
      headerName: t("history.label"),
      width: 150,
      renderCell: (params) => {
        if (!params.value) {
          return null;
        }

        const labelColor = generateColorForChip(params.value);
        return (
          <Chip
            label={params.value}
            color="primary"
            variant="outlined"
            sx={{
              borderColor: labelColor,
              color: labelColor,
              height: "20px",
            }}
          />
        );
      },
    },
    {
      field: "message_status",
      headerName: t("history.status"),
      width: 120,
      renderCell: (params) => {
        const status = params.row.message_status;
        let chipColor = status === "sent" ? "green" : "darkOrange";

        return (
          <Chip
            label={
              messageStatusMapping[params.row.message_status] ||
              params.row.message_status
            }
            color="primary"
            variant="outlined"
            sx={{
              borderColor: chipColor,
              color: chipColor,
              height: "20px",
            }}
          />
        );
      },
    },
    {
      field: "message_sent_timestamp",
      headerName: t("history.messageSent"),
      width: 180,
      valueGetter: (params) =>
        params.row.message_sent_timestamp
          ? formatDate(params.row.message_sent_timestamp)
          : "",
    },
  ];

  useEffect(() => {
    const fetchMessages = async () => {
      const { data, error } = await supabase
        .from("messages")
        .select(
          `
              id,
              to,
              contact_id (
                id,
                first_name,
                last_name,
                nickname
              ),
              group_id (
                id,
                name
              ),
              bulk_contacts,
              bulk_contacts_sent,
              name,
              countrycode_id,
              phone,
              message,
              file_id (
                id,
                name
              ),
              label_id (
                id,
                name
              ),
              message_frequency,
              message_status,
              scheduled_timestamp,
              end_timestamp,
              message_sent_timestamp
            `
        )
        .in("message_status", ["sent", "partial_sent"])
        .is("bulk_contacts_failed", null);

      if (error) {
        showNotification(t("history.errorFetchingMessages"), "error");
      } else {
        const formattedMessages = data.map((message) => ({
          id: message.id,
          type: message.to,
          to:
            message.to === "contact"
              ? `${message.contact_id.first_name} ${message.contact_id.last_name
              }${message.contact_id.nickname
                ? ` (${message.contact_id.nickname})`
                : ""
              }`
              : message.to === "group"
                ? message.group_id.name
                : message.to === "me"
                  ? t("upcoming.myself")
                  : message.name,
          message: message.message,
          file: message.file_id?.name,
          label: message.label_id?.name,
          frequency: message.message_frequency,
          message_status: message.message_status,
          next_at: getLocalTimestamp(message.scheduled_timestamp),
          last_at: getLocalTimestamp(message.end_timestamp),
          contact_id: message.contact_id?.id,
          group_id: message.group_id?.id,
          bulk_contacts: message.bulk_contacts,
          name: message.name,
          countrycode_id: message.countrycode_id,
          phone: message.phone,
          file_id: message.file_id?.id,
          label_id: message.label_id?.id,
          bulk_contacts_sent: message.bulk_contacts_sent,
          message_sent_timestamp: getLocalTimestamp(
            message.message_sent_timestamp
          ),
        }));

        formattedMessages.sort(
          (a, b) =>
            new Date(b.message_sent_timestamp) -
            new Date(a.message_sent_timestamp)
        );

        setMessages(formattedMessages);
      }
    };

    fetchMessages();
  }, [messageModalOpen]);

  const handleCloseMessagesModal = () => {
    trackEvent("history-message_modal_closed");
    setMessageModalOpen(false);
  };

  const handleRowClick = (row) => {
    trackEvent("history-row_clicked");
    setSelectedMessage(row);
    setMessageModalOpen(true);
  };

  return (
    <Box>
      <Box
        sx={{
          height: "auto",
          minHeight: "400px",
          width: "100%",
          "& .MuiDataGrid-root": {
            border: "none",
            bgcolor: "transparent",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: theme.palette.mode === 'dark' ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.mode === 'dark' ? "rgba(0,0,0,0.2)" : "#f8f9fa",
            borderBottom: "none",
            color: theme.palette.mode === 'dark' ? "#a1a4ab" : "#141b2d",
            fontWeight: "bold",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: "transparent",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: theme.palette.mode === 'dark' ? "rgba(0,0,0,0.2)" : "#f8f9fa",
            borderBottomLeftRadius: "16px",
            borderBottomRightRadius: "16px",
          },
          "& .MuiCheckbox-root": {
            color: `${theme.palette.mode === 'dark' ? "#4cceac" : "#3da58a"} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.mode === 'dark' ? "#a1a4ab" : "#727681"} !important`,
            fontWeight: "bold",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)",
          },
        }}
      >
        <DataGrid
          rows={messages}
          columns={columns}
          onRowClick={(params) => handleRowClick(params.row)}
          autoHeight
          disableSelectionOnClick
          sx={{ borderRadius: "16px", overflow: "hidden" }}
          components={{
            Toolbar: () => (
              <GridToolbarContainer sx={{ p: 2, borderBottom: theme.palette.mode === 'dark' ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.05)", gap: 1 }}>
                <Box sx={{ flexGrow: 1 }} />
                <GridToolbarFilterButton />
                <GridToolbarColumnsButton />
                <GridToolbarDensitySelector />
                <GridToolbarExport />
              </GridToolbarContainer>
            ),
          }}
        />
      </Box>

      <Message
        open={messageModalOpen}
        onClose={handleCloseMessagesModal}
        message={selectedMessage}
        readOnly={true}
        showButton={true}
      />
    </Box>
  );
}
